import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Service, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    AuthError,
    BrowserAuthErrorCodes,
    CacheLookupPolicy,
    EventMessage,
    EventType,
    InteractionRequiredAuthError,
    InteractionStatus,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import { ContextStateService } from '@core/services/context-state.service';
import { environment } from '@environment';
import { SessionExpiredOverlayComponent } from '@shared/components/session-expired-overlay/session-expired-overlay.component';
import { catchError, combineLatest, filter, firstValueFrom, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

@Service()
export class AuthenticationService {
    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private readonly msalService = inject(MsalService);
    private readonly contextStateService = inject(ContextStateService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly overlay = inject(Overlay);
    public readonly isUserLoggedIn$: Observable<boolean>;

    public readonly showSessionExpiredLogin = signal<boolean>(false);
    private readonly isActiveAccountSet = signal<boolean>(null);
    private readonly isActiveAccountValid = signal<boolean>(null);
    private readonly isActiveAccountValid$ = toObservable(this.isActiveAccountValid);
    private sessionExpiredOverlayRef: OverlayRef | null = null;

    /** Serializes SignalR hub token acquisition (negotiation / reconnect; shared across hubs to avoid parallel MSAL popups). */
    private signalrHubTokenChain: Promise<unknown> = Promise.resolve();

    constructor() {
        this.isUserLoggedIn$ = toObservable(this.isActiveAccountSet);
    }

    public setIsActiveAccountValid(): Observable<boolean> {
        return this.isUserLoggedIn$.pipe(
            switchMap(isLoggedIn => {
                if (isLoggedIn) {
                    return this.acquireTokenSilent();
                }

                return of(true);
            }),
            take(1),
            catchError(error => {
                if (error instanceof InteractionRequiredAuthError) {
                    return of(false);
                }

                return of(true);
            }),
            tap(result => {
                if (typeof result === 'object' || (typeof result === 'boolean' && result)) {
                    this.isActiveAccountValid.set(true);
                } else {
                    this.isActiveAccountValid.set(false);
                }
            }),
            map(() => true)
        );
    }

    public isUserLoggedInWithActiveSession(): Observable<boolean> {
        return combineLatest([this.isUserLoggedIn$, this.isActiveAccountValid$]).pipe(
            filter(([isLoggedIn, isAccountValid]) => isLoggedIn !== null && isAccountValid != null),
            map(([isLoggedIn, isAccountValid]) => isLoggedIn && isAccountValid)
        );
    }

    public init(appDestroyed: Subject<void>): void {
        this.initActiveAccount(appDestroyed);
    }

    public loadCurrentContext(): void {
        this.contextStateService.loadCurrentContext(false);
    }

    public acquireTokenSilent(): Observable<AuthenticationResult> {
        const active = this.msalService.instance.getActiveAccount();
        const request: SilentRequest = this.getSilentRequest(active);

        return this.msalService.acquireTokenSilent(request);
    }

    /**
     * Token for SignalR hubs (not HTTP): wait until MSAL is idle, silent acquire with refresh allowed, then popup/login if needed.
     */
    public acquireAccessTokenForSignalRHub(): Promise<string> {
        const run = (): Promise<string> => this.acquireAccessTokenForSignalRHubInternal();
        const next = this.signalrHubTokenChain.then(run);
        this.signalrHubTokenChain = next.then(
            () => undefined,
            () => undefined
        );
        return next;
    }

    public logout() {
        this.msalService.logoutRedirect();
    }

    public loginPopup(): void {
        this.msalService
            .loginPopup({
                ...this.msalGuardConfig.authRequest,
                scopes: [...environment.apiConfig.api.scopes],
                loginHint: this.contextStateService.currentUser()?.email,
                authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
            })
            .subscribe();
    }

    public checkAndSetActiveAccount() {
        const beforeActive = this.msalService.instance.getActiveAccount();
        const all = this.msalService.instance.getAllAccounts();

        let activeAccount = beforeActive;
        if (activeAccount == null) {
            if (all.length > 0) {
                this.setActiveAccount(all[0]);
            } else {
                this.isActiveAccountSet.set(false);
            }
        } else {
            this.isActiveAccountSet.set(true);
        }
    }

    public changePassword(): void {
        const authority = `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.changePasswordPolicyName}`;
        const clientId = environment.msalConfig.auth.clientId;
        const scopes = [...environment.apiConfig.api.scopes];

        const lang = this.contextStateService.currentUser()?.language.split('-')[0] || 'en';
        const redirectUri = encodeURIComponent(window.location.origin);
        const state = encodeURIComponent(JSON.stringify({ id: 'your_state_id', metadata: { interactionType: 'redirect' } }));
        const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => ('0' + b.toString(16)).slice(-2))
            .join('');
        const nonceEncoded = encodeURIComponent(nonce);
        const codeVerifier = Array.from(crypto.getRandomValues(new Uint8Array(32)))
            .map(b => ('0' + b.toString(16)).slice(-2))
            .join('');
        const codeChallenge = btoa(codeVerifier); // Base64 URL-encode the verifier
        const codeChallengeEncoded = encodeURIComponent(codeChallenge);
        const codeChallengeMethod = 'S256';

        const authUrl = `${authority}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&scope=${scopes.join(' ')}&redirect_uri=${redirectUri}&response_mode=fragment&state=${state}&nonce=${nonceEncoded}&code_challenge=${codeChallengeEncoded}&code_challenge_method=${codeChallengeMethod}&lang=${lang}`;
        window.open(authUrl, '_blank');
        ///this.msalService
        ///    .loginRedirect({
        ///        scopes: [...environment.apiConfig.api.scopes],
        ///        extraQueryParameters: { lang: this.contextStateService.currentUser()?.language.split('-')[0] },
        ///        authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.changePasswordPolicyName}`,
        ///    })
        ///    .subscribe();
    }

    public test(): void {
        this.msalService
            .loginRedirect({
                scopes: [...environment.apiConfig.api.scopes],
                prompt: 'login',
                loginHint: this.contextStateService.currentUser()?.email,
                extraQueryParameters: { lang: this.contextStateService.currentUser()?.language.split('-')[0] },
                authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
            })
            .subscribe();
    }

    public checkIsUserLoggedIn(): Observable<boolean> {
        return this.msalService.handleRedirectObservable().pipe(
            map(tokenResponse => {
                if (!tokenResponse && !this.isActiveAccountSet()) {
                    return false;
                }
                return true;
            })
        );
    }

    public acceptInvitation(token: string, language: string): void {
        this.msalService
            .loginRedirect({
                scopes: [...environment.apiConfig.api.scopes],
                extraQueryParameters: { id_token_hint: token, lang: language.split('-')[0] },
                authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signUpFromInvitationPolicy}`,
                redirectUri: window.location.origin,
                redirectStartPage: window.location.origin,
            })
            .subscribe();
    }

    public waitUntilMsalIdle(): Promise<void> {
        return this.waitForMsalInteractionNone();
    }

    public async loginRedirectWithLoginHint(loginHint: string, differentThanCurrentUser: boolean, uiLocales?: string): Promise<void> {
        await this.waitForMsalInteractionNone();

        const locale = uiLocales?.trim() || this.contextStateService.currentUser()?.language?.split('-')[0] || environment.translations.defaultLang.split('-')[0];
        const request: RedirectRequest = {
            scopes: [...environment.apiConfig.api.scopes],
            prompt: differentThanCurrentUser ? 'login' : undefined,
            loginHint,
            extraQueryParameters: { ui_locales: locale },
            authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
        };

        if (differentThanCurrentUser) {
            {
                request.prompt = 'login';
            }
        }
        this.msalService.loginRedirect(request).subscribe();
    }

    private async acquireAccessTokenForSignalRHubInternal(): Promise<string> {
        await this.waitForMsalInteractionNone();

        const account = this.msalService.instance.getActiveAccount();
        const request: SilentRequest = this.getSilentRequest(account);

        try {
            const result = await firstValueFrom(this.msalService.acquireTokenSilent(request));

            return result.accessToken;
        } catch (err) {
            console.error('[AskemoAuth] Silent token acquisition failed for SignalR hub; falling back to interactive method', { error: err });
            await this.waitForMsalInteractionNone();

            const base = {
                scopes: [...environment.apiConfig.api.scopes] as string[],
                authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
                extraQueryParameters: {
                    ui_locale: this.contextStateService.currentUser()?.language?.split('-')[0] ?? 'en',
                },
            };

            if (account) {
                const popupResult = await firstValueFrom(this.msalService.acquireTokenPopup({ ...this.msalGuardConfig.authRequest, ...base, account }));
                return popupResult.accessToken;
            }

            const loginResult = await firstValueFrom(
                this.msalService.loginPopup({
                    ...this.msalGuardConfig.authRequest,
                    ...base,
                })
            );
            return loginResult.accessToken;
        }
    }

    private waitForMsalInteractionNone(): Promise<void> {
        return firstValueFrom(
            this.msalBroadcastService.inProgress$.pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                take(1),
                map(() => undefined)
            )
        );
    }

    private initActiveAccount(appDestroyed: Subject<void>): void {
        this.msalBroadcastService.inProgress$.pipe(takeUntil(appDestroyed)).subscribe((status: InteractionStatus) => {
            if (status === InteractionStatus.None) {
                this.checkAndSetActiveAccount();
            }
        });
        this.msalBroadcastService.msalSubject$.pipe(takeUntil(appDestroyed)).subscribe((msg: EventMessage) => {
            if (msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS) {
                const payload = msg.payload as AuthenticationResult;
                this.setActiveAccount(payload?.account);
                this.closeSessionExpiredOverlay();
            }

            if (msg.eventType === EventType.POPUP_OPENED) {
                this.openSessionExpiredOverlay();
            }

            if (
                msg.eventType === EventType.LOGIN_SUCCESS &&
                this.contextStateService.currentUser() &&
                msg.payload &&
                (msg.payload as AuthenticationResult).account.name &&
                (msg.payload as AuthenticationResult).account.name !== this.contextStateService.currentUser().email
            ) {
                window.location.href = window.location.origin;
            }

            if (msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE || msg.eventType === EventType.LOGIN_FAILURE) {
                if (
                    msg.error instanceof AuthError &&
                    [BrowserAuthErrorCodes.popupWindowError, 'access_denied'].includes(msg.error.errorCode) &&
                    !this.contextStateService.currentUser()
                ) {
                    this.msalService
                        .loginRedirect({
                            ...this.msalGuardConfig.authRequest,
                            authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
                            scopes: [...environment.apiConfig.api.scopes],
                        })
                        .subscribe();
                } else {
                    this.showSessionExpiredLogin.set(
                        msg.error instanceof AuthError &&
                            [BrowserAuthErrorCodes.popupWindowError, BrowserAuthErrorCodes.userCancelled, 'access_denied'].includes(msg.error.errorCode)
                    );
                    this.openSessionExpiredOverlay();
                }
            }
        });
    }

    private async openSessionExpiredOverlay(): Promise<void> {
        if (this.sessionExpiredOverlayRef) {
            return;
        }

        this.sessionExpiredOverlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'session-expired-backdrop',
            panelClass: 'session-expired-panel',
            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        });

        this.sessionExpiredOverlayRef.attach(new ComponentPortal(SessionExpiredOverlayComponent));
    }

    private closeSessionExpiredOverlay(): void {
        this.sessionExpiredOverlayRef?.detach();
        this.sessionExpiredOverlayRef = null;
    }

    private setActiveAccount(account: AccountInfo) {
        if (account) {
            this.msalService.instance.setActiveAccount(account);
            this.isActiveAccountSet.set(true);
        }
    }

    private getSilentRequest(activeAccount: AccountInfo): SilentRequest {
        const request: SilentRequest = {
            ...this.msalGuardConfig.authRequest,
            authority: `https://${environment.msalConfig.auth.identityUrl}/${environment.msalConfig.auth.tenantId}/${environment.msalConfig.auth.signInPolicyName}`,
            scopes: [...environment.apiConfig.api.scopes],
            extraQueryParameters: { ui_locale: this.contextStateService.currentUser()?.language.split('-')[0] },
            account: activeAccount,
            cacheLookupPolicy: CacheLookupPolicy.Default,
        };

        return request;
    }

    // private isTokenIssuedFromPolicy(idToken: IdTokenClaimsWithPolicyId, policyName: string) {
    //     return idToken.acr?.toLocaleLowerCase() === policyName.toLocaleLowerCase() || idToken.tfp?.toLocaleLowerCase() === policyName.toLocaleLowerCase();
    // }
}
